import React from 'react';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
}

const crumbLink =
  'inline-flex items-center gap-1 text-gray-500 hover:text-gray-900 ' +
  'dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors';

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  showHome?: boolean;
  onHomeClick?: () => void;
  className?: string;
}

/**
 * Breadcrumb trail. All crumbs share the same size (text-sm) and family —
 * the current page is differentiated only by weight + ink colour, not size.
 */
export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  showHome = true,
  onHomeClick,
  className,
}) => {
  const allItems: BreadcrumbItem[] = showHome
    ? [
        {
          label: 'Главная',
          onClick: onHomeClick,
          icon: <Home className="h-3.5 w-3.5" />,
        },
        ...items,
      ]
    : items;

  return (
    <nav
      className={cn('flex items-center text-sm dark:text-zinc-400', className)}
      aria-label="Breadcrumb"
    >
      <ol className="flex items-center gap-1.5">
        {allItems.map((item, index) => {
          const isLast = index === allItems.length - 1;
          return (
            <li key={index} className="flex items-center gap-1.5">
              {index > 0 && (
                <ChevronRight
                  className="h-3.5 w-3.5 text-gray-300 dark:text-zinc-600 shrink-0"
                  aria-hidden="true"
                />
              )}

              {isLast ? (
                <span className="inline-flex items-center gap-1 text-gray-900 dark:text-zinc-100 font-medium">
                  {item.icon}
                  {item.label}
                </span>
              ) : item.href ? (
                // Настоящая ссылка: работает средний клик и «открыть в новой
                // вкладке». Обычный клик перехватываем, чтобы не перезагружать
                // приложение целиком.
                <a
                  href={item.href}
                  onClick={(event) => {
                    if (!item.onClick) return;
                    event.preventDefault();
                    item.onClick();
                  }}
                  className={crumbLink}
                >
                  {item.icon}
                  {item.label}
                </a>
              ) : (
                <button onClick={item.onClick} className={crumbLink}>
                  {item.icon}
                  {item.label}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
